import { Edit as EditIcon, LockClock, Person } from "@mui/icons-material";
import { IconButton, Typography } from "@mui/material";
import { intlFormatDateTimeWithWeekday } from "helpers";
import { PlannedDateEditDialog } from "pages/jobs/dialogs/PlannedDateEditDialog";
import { FC, useCallback, useMemo } from "react";
import { useIntl } from "react-intl";
import { useAppDispatch, useAppSelector } from "store";
import { selectPageSelectedJob } from "store/root.store";
import { setUpdatePlannedDateOpen } from "store/slices/jobs.store";

interface DetailsTimeDisplayProps {
  readonly?: boolean;
}

export const DetailsTimeDisplay: FC<DetailsTimeDisplayProps> = (props: DetailsTimeDisplayProps) => {
  const { readonly } = props;
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const job = useAppSelector(selectPageSelectedJob);

  const plannedDate = job.plannedDate;
  const responseDate = job.responseDate;

  const editPlannedString = useMemo(
    () =>
      job.plannedDate?.startTime || job.plannedDate?.stopTime
        ? intl.formatMessage({ id: "visit.changePlannedTimes" })
        : intl.formatMessage({ id: "visit.addPlannedTimes" }),
    [intl, job.plannedDate?.startTime, job.plannedDate?.stopTime],
  );

  const responseString = useMemo(
    () =>
      responseDate ? (
        <>
          <LockClock className="left-icon-adjust" sx={{ mr: 1.75 }} />
          {intlFormatDateTimeWithWeekday(intl, responseDate)}
        </>
      ) : (
        <></>
      ),
    [intl, responseDate],
  );

  const labelDisplay = useCallback(() => {
    if (plannedDate) {
      if (plannedDate.startTime) return intl.formatMessage({ id: "visit.planned" });
    } else if (responseDate) {
      return responseString;
    }

    return intl.formatMessage({ id: "general.unplanned" });
  }, [intl, plannedDate, responseDate, responseString]);

  return (
    <>
      <Typography
        variant="h6"
        sx={{
          fontSize: "1.2rem",
        }}
        color="black"
        data-testid="DetailsTimeDisplay"
      >
        {labelDisplay()}
        {!readonly && (
          <IconButton
            aria-label={editPlannedString}
            onClick={() => dispatch(setUpdatePlannedDateOpen({ open: true }))}
            data-testid="DetailsTimeDisplay-EditPlannedButton"
            sx={{
              fontSize: "1.2rem",
              paddingY: 0,
            }}
          >
            <EditIcon color="primary" />
          </IconButton>
        )}
      </Typography>

      {plannedDate?.startTime && (
        <>
          <Typography sx={{ marginLeft: "38px" }}>
            {intlFormatDateTimeWithWeekday(intl, plannedDate.startTime)}
          </Typography>
          {plannedDate.stopTime && (
            <>
              <Typography sx={{ marginLeft: "38px" }}>
                {intl.formatMessage({ id: "times.to" })}
              </Typography>
              <Typography sx={{ marginLeft: "38px" }}>
                {intlFormatDateTimeWithWeekday(intl, plannedDate.stopTime)}
              </Typography>
            </>
          )}
        </>
      )}
      {job.receivedDate && (
        <Typography variant="body2" color="secondary">
          <LockClock className="left-icon-adjust" sx={{ mr: 1.75 }} />
          {`${intl.formatMessage({
            id: "job.receivedDate",
          })}: ${intlFormatDateTimeWithWeekday(intl, job.receivedDate)}`}
        </Typography>
      )}
      {job.taskCreator && (
        <Typography variant="body2" color="secondary">
          <Person className="left-icon-adjust" sx={{ mr: 1.75 }} />
          {`${intl.formatMessage({
            id: "job.receivedBy",
          })}: ${job.taskCreator}`}
        </Typography>
      )}
      {plannedDate && job.responseDate && (
        <Typography variant="body2" color="secondary">
          <LockClock className="left-icon-adjust" sx={{ mr: 1.75 }} />
          {`${intl.formatMessage({
            id: "job.responseDate",
          })}: ${intlFormatDateTimeWithWeekday(intl, job.responseDate)}`}
        </Typography>
      )}

      {!readonly && <PlannedDateEditDialog />}
    </>
  );
};
